import type React from 'react';
import { useTranslation } from 'react-i18next';
import { AssetFormDrawer } from '@/pages/accounting/registers/fixedAssets/components/assetFormDrawer/AssetFormDrawer';
import { useAssetCategoriesQuery, useChartOfAccounts } from '@/api/queries';
import {
    resolveLocale,
    unwrapStatement,
} from '@/pages/accounting/statements/statementMeta';
import { PATH } from '@/router/PATH';
import type { ActionSurfaceProps } from '../../controlCenterSurfaces';

/**
 * "Add asset" from the launcher — the Fixed Assets register's **own** drawer.
 *
 * Serves itself the categories and the chart rather than taking them as
 * props, the same reasoning `AddEmployeeSurface` gives for tax rates: both
 * queries are shared and cached, and needing to know about them to open a
 * launcher is exactly the coupling this seam exists to avoid.
 *
 * The message is generic — the drawer's own `onSaved` fires with no
 * arguments, so there is no created asset's name to interpolate.
 */
export const AddAssetSurface: React.FC<ActionSurfaceProps> = ({
    onClose,
    onDone,
}) => {
    const { i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);
    const categoriesQuery = useAssetCategoriesQuery();
    const chart = useChartOfAccounts({ locale });

    const { data: categoriesData } = unwrapStatement(
        categoriesQuery.data,
        categoriesQuery.error,
    );
    const categories = categoriesData?.items ?? [];

    return (
        <AssetFormDrawer
            accounts={chart.data ?? []}
            asset={null}
            categories={categories}
            isOpen
            locale={locale}
            onClose={onClose}
            onSaved={() =>
                onDone({
                    messageKey: 'controlCenter.outcome.assetAdded',
                    to: PATH.ACCOUNTING.REGISTERS.FIXED_ASSETS,
                    linkKey: 'controlCenter.outcome.openFixedAssets',
                })
            }
        />
    );
};
